import { track } from "@/utils/events/tracker";
import { axiosGet } from "./axiosWrapper";

// Checks Supabase's public status page API for overall platform health
// Reference: https://status.supabase.com/api/v2/status.json
const isSupabasePlatformOperational = async (): Promise<boolean> => {
  // Using the new wrapper function with custom error handling
  const result = await axiosGet(
    "https://status.supabase.com/api/v2/status.json",
    {
      errorMessage: "Error checking Supabase platform status",
      showToast: false, // Don't show toast for this check
      throwOnError: false, // Don't throw, we want to handle it
    }
  );

  if (!result) {
    track("SUPABASE_STATUS_CHECK_ERROR", { error: "Failed to fetch status" });
    return false;
  }

  return true;
};

export const checkRedisHealth = async (
  canvasId: string,
  supabaseClient: any
): Promise<{
  confirmedRedisDown: boolean;
  anyCheckFailed: boolean;
}> => {
  if (!supabaseClient) {
    return {
      confirmedRedisDown: false,
      anyCheckFailed: false,
    };
  }

  const checkRedisIsUp = async (
    canvasId: string,
    supabaseClient: any
  ): Promise<boolean> => {
    const redisHealthUrl = `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/redis-health`;

    // Using the new wrapper function
    const result = await axiosGet(redisHealthUrl, {
      errorMessage: "Error checking redis health",
      showToast: false, // Don't show toast for health checks
      throwOnError: false, // Don't throw, we want to handle it
    });

    if (!result) {
      track("REDIS_HEALTH_ERROR", { error: "Failed to fetch redis health" });
      return false;
    }

    return true;
  };

  let redisIsUp = await checkRedisIsUp(canvasId, supabaseClient);
  let attempts = 1;

  let anyCheckFailed = !redisIsUp;

  // Check 3 times until it's up with a 1 second delay between each check
  while (attempts < 3 && !redisIsUp) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    redisIsUp = await checkRedisIsUp(canvasId, supabaseClient);

    anyCheckFailed = anyCheckFailed || !redisIsUp;
    attempts++;
  }

  // Only confirm Redis is down if Supabase platform is operational.  This helps
  // avoid blaming Redis when the user is having a general network issue on
  // their end.
  let confirmedRedisDown = false;
  if (!redisIsUp) {
    const supabaseOk = await isSupabasePlatformOperational();
    confirmedRedisDown = !redisIsUp && supabaseOk;
  }

  return {
    confirmedRedisDown,
    anyCheckFailed,
  };
};
